// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Piano Intervals
//
// Include description of object & useful info here, please!

// Question format: G4/P5 (note/interval), Ab3/-d5, etc...

// Interval name type options:
//    longNames: Perfect 4th
//    genericNames: 4th
//    halfWholeNames: Half Step, Whole Step, minor third, etc...
//    ascendingDescending: ascending P4
//      The default is: (intervalname) + above/below.

/*
  options: {
    lowestNote: "B3",
    highestNote: "C5",
    autoRange: defaults true if lowestNote & highestNote not set.
    questionPrompt: "Play the note {question} the highlighted note.",
    drill: true,
    skills: ["half_steps_piano", "whole_steps_piano"],
    maxQuestions: 10,
    halfWholeNames: true; longNames: true, genericNames: true, directionOnly
    autoadvance: true,
    forceSkill: 1
  }
*/
import InteractivePrototype from './interactivePrototype.ts';
import * as Skills from '../uTheory.Skills/skills.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import * as Notation from '../notation.ts';
import utils from '../utils.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import config from '../config.ts';
import { toNoteNamesString } from '../uTheory.Notation/ariaMidiHelpers.ts';

class PianoIntervals extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeedFromSkills = PianoIntervals.registry
      .map((r) => r.skills)
      .reduce((a, b) => a.concat(b));
    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    options.maxQuestions = options.maxQuestions || 10;
    this.settings = {
      instructions: true,
      piano: {
        ariaTitle: () => {
          if (!this.piano) {
            return;
          }
          const intervalText = this.currentQuestion.interval.toString()

          return `${
            this.piano.getAriaTitle(true)
          } play the note ${
            this.options.directionOnly ? 'an octave' : ''
          } ${this.currentQuestion.text} ${this.options.directionOnly ? '' : 'it'}`;
        },
        ariaLive: 'alert',
      },
      checkMark: {
        rightAnswerLength: 1,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.layout = {
      instructions: {
        maxPercent: 30,
      },
    };

    if (!options.lowestNote || !options.highestNote) {
      options.autoRange = true;
      options.lowestNote = 'C4'; // set something just to be safe
      options.highestNote = 'C5'; // ditto
    }

    // If we're in testMode, we need to give them the option to
    // undo a click & submit when ready.
    if (options.testMode) {
      this.settings.letters = {
        letterButtons: false,
        customButtons: [
          { text: 'undo', hotKey: 'delete', value: 'undo' },
          { text: 'submit', hotKey: 'enter', value: 'submit' },
        ],
      };
    }

    // copy our settings onto the prototype settings:
    utils.merge(this.settings.piano, {
      lowestNote: options.lowestNote,
      highestNote: options.highestNote,
    });

    // now call the prototype's init function:
    super.init(displayDiv, options);
  }

  generateQuestions(questions) {
    this.questionPrompt = this.options.questionPrompt || '<span role="alert" aria-live="polite">Play {question} the highlighted note.</span>';

    // Either generate or calculate questions:
    if (typeof this.options.drill !== 'undefined' && this.options.drill === true) {
      questions = this.randomQuestionGenerator();
    } else {
      questions = this.prepareManualQuestions(questions);
    }

    this.questions = questions.map((question) => {
      const text = this.questionText(question.interval, question.note);
      const skill = this.calculateSkill(question.interval);

      return {
        note: question.note,
        interval: question.interval,
        text,
        skills: [].concat(skill),
      };
    }, this);

    this.note = new Notation.Note();
    this.maxQuestions = this.questions.length;
  }

  randomQuestionGenerator() {
    const generator = new QuestionGenerator();
    return generator.multipleSkillsGenerate(
      this.options.skills,
      this.options.maxQuestions,
      this.options
    );
  }

  prepareManualQuestions(questions) {
    questions = questions || this.options.questions;
    return questions.map((question) => {
      const questionParts = question.split('/');
      const note = new Notation.Note(questionParts[0]);
      const interval = new Notation.Interval(questionParts[1]);
      return {
        note,
        interval,
      };
    }, this);
  }

  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.clearCheckpointAnswer();
      this.generateQuestions([questionString]);
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  calculateSkill(interval) {
    if (typeof this.options.forceSkill !== 'undefined') {
      return Skills.makeInt(this.options.forceSkill);
    }
    // otherwise, invoke a skillCalculator:
    if (typeof this.skillCalculator === 'undefined') {
      this.skillCalculator = new SkillCalculator();
    }

    if (
      typeof this.options.halfWholeNames !== 'undefined' &&
      this.options.halfWholeNames === true &&
      interval.isLessThan('m3')
    ) {
      return this.skillCalculator.getPianoHalfWholeSkill(interval);
    }

    return this.skillCalculator.getPianoIntervalSkill(interval);
    // GCR: Expand logic to other intervals later
  }

  questionText(interval, note) {
    const intervalText = this.intervalText(interval);
    const directionText = this.directionText(interval);
    const noteText = toNoteNamesString(note, true);
    const ascDsc =
      typeof this.options.ascendingDescending !== 'undefined' &&
      this.options.ascendingDescending === true;

    if (typeof this.options.directionOnly !== 'undefined' && this.options.directionOnly === true) {
      return directionText + `<span class="sr-only">&nbsp;${noteText}</span>`;
    }

    if (ascDsc) {
      return `${utils.aOrAn(directionText)} ${directionText} ${intervalText}<span class="sr-only">&nbsp;from ${noteText}</span>`;
    }
    return `${utils.aOrAn(intervalText)} ${intervalText} <strong>${directionText}</strong><span class="sr-only">&nbsp;${noteText}</span>`;
  }

  intervalText(interval) {
    // longNames: Perfect 4th, minor 2nd, Augmented octave
    if (typeof this.options.longNames !== 'undefined' && this.options.longNames === true) {
      return interval.longName();
    }
    if (
      typeof this.options.halfWholeNames !== 'undefined' &&
      this.options.halfWholeNames === true
    ) {
      return interval.halfWholeName();
    }
    if (typeof this.options.genericNames !== 'undefined' && this.options.genericNames === true) {
      return interval.longGenericName();
    }
    return interval.quality + interval.genericInterval;
  }

  directionText(interval) {
    if (
      typeof this.options.ascendingDescending !== 'undefined' &&
      this.options.ascendingDescending === true
    ) {
      return interval.direction > 0 ? 'ascending' : 'descending';
    }
    return interval.direction > 0 ? 'above' : 'below';
  }

  newQuestion() {
    super.newQuestion();
    this.piano.clearColors();
    this.note = this.questions[this.questionNumber].note;
    this.interval = this.questions[this.questionNumber].interval;
    if (this.options.autoRange) this.autoRange();
    const newMidi = this.note.getMidiNumber();
    console.log({newMidi, note: this.note})
    this.piano.colorNote(newMidi);
  }

  // Checks to make sure all of the notes in the melody are playable
  // on the given keyboard:
  autoRange() {
    const { note } = this;
    const ascInterval = this.interval.direction === 1;
    const generic = this.interval.isLessThan('P8') ? 9 : this.interval.genericInterval + 2;

    let lowestPianoNote;
    let highestPianoNote;
    if (ascInterval) {
      lowestPianoNote = note.getNoteAtGeneric(-2);
      highestPianoNote = note.getNoteAtGeneric(generic);
    } else {
      highestPianoNote = note.getNoteAtGeneric(2);
      lowestPianoNote = note.getNoteAtGeneric(-generic);
    }

    this.piano.setBoundaries(lowestPianoNote, highestPianoNote);
  }

  addListeners() {
    if (!this.options.testMode) {
      this.piano.addNoteOnListener(this.check, this);
    } else {
      this.piano.addNoteOnListener(this.checkpointNoteOn, this);
      this.letterPanel.addCustomButtonCallback((value) => {
        if (value === 'undo') this.clearCheckpointAnswer();
        if (value === 'submit') this.submitCheckpointAnswer();
      }, this);
    }
  }

  check(midiNumber) {
    if (this.rightAnswerDelay) return undefined;
    const goalNote = this.note.getNoteAt(this.interval);
    this.goalMIDI = goalNote.getMidiNumber();

    if (midiNumber === this.note.getMidiNumber()) {
      this.checkMark.warn('Just play the 2nd note of the interval.');
      return undefined;
    }
    if (this.interval.direction === 1 && this.note.getMidiNumber() > midiNumber) {
      this.checkMark.warn('Play an ASCENDING interval.');
      return undefined;
    }
    if (this.interval.direction === -1 && this.note.getMidiNumber() < midiNumber) {
      this.checkMark.warn('Play a DESCENDING interval.');
      return undefined;
    }

    if (this.options.testMode) return this.checkpointAnswer(midiNumber);

    const correct = this.goalMIDI === midiNumber;
    if (correct) {
      this.piano.colorNote(midiNumber, undefined, true);
      this.rightAnswer(1000);
    } else this.wrongAnswer();
    return undefined;
  }

  // Checks the current question, and returns a data set
  // based on however the student answered.
  getAnswerAndData(midiNumber) {
    midiNumber = midiNumber || null; // for force submit, so we don't
    // submit an undefined.
    const correct = this.goalMIDI === midiNumber;
    if (correct) {
      this.piano.colorNote(midiNumber);
    } else if (midiNumber) {
      this.piano.colorNote(midiNumber, config.Colors.lightRed);
    }

    return {
      question: this.instructionsDiv.innerHTML,
      questionType: 'PianoIntervals',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: null,
      studentAnswerSVG: this.piano.exportSVG(),
      correctAnswerText: this.note.getNoteAt(this.interval).toShortString(),
      rightAnswers: 1 * correct,
      wrongAnswers: 1 * !correct,
      skills: this.questions[this.questionNumber].skills,
    };
  }

  checkpointNoteOn(midiNumber) {
    this.clearCheckpointAnswer();
    this.checkpointNotePressed = midiNumber;
    this.piano.colorNote(midiNumber);
  }

  clearCheckpointAnswer() {
    if (this.checkpointNotePressed && this.checkpointNotePressed !== this.note.getMidiNumber()) {
      this.piano.clearNoteColoring(this.checkpointNotePressed);
    }
    this.checkpointNotePressed = undefined;
  }

  submitCheckpointAnswer() {
    if (!this.checkpointNotePressed) {
      return this.checkMark.warn('You must play an answer note first.');
    }
    this.check(this.checkpointNotePressed);
    return undefined;
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.piano.removeListeners();
    super.gameOver();
  }

  debugRightAnswer() {
    this.goalMIDI = this.note.getNoteAt(this.interval).getMidiNumber();
    this.rightAnswer();
  }

  playAnswer() {
    this.player.play(this.note.getMidiNumber(), { startTime: 0, stopTime: 0.5 });
    this.player.play(this.goalMIDI, { startTime: 0.5, stopTime: 1.25 });
  }
}

PianoIntervals.registry = [
  {
    options: {
      lowestNote: 'G3',
      highestNote: 'E5',
    },
    description: 'Play Intervals',
    skills: [
      'ascending_major_perfect',
      'ascending_others',
      'descending_minor_perfect',
      'descending_others',
    ],
  },
  {
    options: {
      lowestNote: 'G3',
      highestNote: 'E5',
      halfWholeNames: true,
    },
    description: 'Play Half and Whole Steps',
    skills: ['half_steps_piano', 'whole_steps_piano'],
  },
];

export default PianoIntervals;
